import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from 'framer-motion';
import {
  X,
  ChevronDown,
  TrendingUp,
  TrendingDown,
  Clock,
  Cloud,
  BarChart2,
  Swords,
  ExternalLink,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useAppStore } from '../../store/useAppStore';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import type { SportRecommendation } from '../../types';

// ─── Snap heights as % of viewport ───────────────────────────────────────────
const SNAP_60 = 0.6;
const SNAP_90 = 0.9;

const DECISION_STYLES: Record<string, { label: string; cls: string }> = {
  STRONG_BET:    { label: 'Strong Bet',    cls: 'bg-win/20 text-win border-win/30'            },
  QUALIFIED_BET: { label: 'Qualified Bet', cls: 'bg-brand/20 text-brand border-brand/30'      },
  LEAN:          { label: 'Lean',          cls: 'bg-warn/20 text-warn border-warn/30'          },
  NO_BET:        { label: 'No Bet',        cls: 'bg-surface-3 text-gray-500 border-surface-4' },
  INVALID:       { label: 'Invalid',       cls: 'bg-loss/20 text-loss border-loss/30'          },
};

const TIMING_STYLES: Record<string, { label: string; cls: string }> = {
  BET_NOW: { label: 'Bet Now', cls: 'text-gold-bright' },
  WAIT:    { label: 'Wait',    cls: 'text-warn'         },
  MONITOR: { label: 'Monitor', cls: 'text-brand'        },
  AVOID:   { label: 'Avoid',   cls: 'text-loss'         },
};

type Tab = 'overview' | 'markets' | 'ai' | 'matchup';
const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'markets',  label: 'Markets'  },
  { id: 'ai',       label: 'AI'       },
  { id: 'matchup',  label: 'Matchup'  },
];

// ─── Tab body ─────────────────────────────────────────────────────────────────

function OverviewTab({ rec }: { rec: SportRecommendation }) {
  const confidence = Math.round((rec.confidence ?? 0) * 100);
  const edge = rec.edge ?? 0;
  const decision = DECISION_STYLES[rec.decisionStatus] ?? DECISION_STYLES.NO_BET;
  const timing = TIMING_STYLES[rec.timing] ?? TIMING_STYLES.MONITOR;

  return (
    <div className="space-y-4 p-4">
      {/* Decision + timing */}
      <div className="flex items-center gap-3">
        <span className={clsx('px-2.5 py-1 rounded-md border text-xs font-semibold', decision.cls)}>
          {decision.label}
        </span>
        <div className="flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5 text-gray-500" />
          <span className={clsx('text-xs font-semibold', timing.cls)}>{timing.label}</span>
        </div>
      </div>

      {/* Odds / market */}
      <div className="card p-3 space-y-2">
        <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">Selection</div>
        <div className="text-sm font-semibold text-gray-100">{rec.selection}</div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">{rec.market}</span>
          <span className={clsx('font-mono font-bold tabular-nums text-sm', rec.odds > 0 ? 'text-win' : 'text-gray-200')}>
            {rec.odds > 0 ? '+' : ''}{rec.odds}
          </span>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-3">
        <div className="card p-3 text-center">
          <div className="text-xs text-gray-500 mb-1">Confidence</div>
          <div className={clsx('font-mono text-lg font-bold tabular-nums',
            confidence >= 70 ? 'text-win' : confidence >= 55 ? 'text-warn' : 'text-gray-400')}>
            {confidence}%
          </div>
          <div className="mt-2 h-1.5 bg-surface-3 rounded-full overflow-hidden">
            <div
              className={clsx('h-full rounded-full', confidence >= 70 ? 'bg-win' : confidence >= 55 ? 'bg-warn' : 'bg-gray-500')}
              style={{ width: `${confidence}%` }}
            />
          </div>
        </div>
        <div className="card p-3 text-center">
          <div className="text-xs text-gray-500 mb-1">Edge</div>
          <div className="flex items-center justify-center gap-1">
            {edge >= 0
              ? <TrendingUp className="w-4 h-4 text-win" />
              : <TrendingDown className="w-4 h-4 text-loss" />}
            <span className={clsx('font-mono text-lg font-bold tabular-nums',
              edge >= 3 ? 'text-win' : edge >= 1 ? 'text-warn' : 'text-gray-400')}>
              {edge >= 0 ? '+' : ''}{edge.toFixed(1)}%
            </span>
          </div>
        </div>
      </div>

      {/* AI summary placeholder */}
      <div className="card p-3 border-purple/20 bg-purple/5">
        <div className="flex items-center gap-2 mb-1.5">
          <Swords className="w-3.5 h-3.5 text-purple-bright" />
          <span className="text-xs font-medium text-purple-bright">AI Analysis</span>
        </div>
        <p className="text-xs text-gray-400 leading-relaxed">
          {rec.decisionStatus === 'STRONG_BET'
            ? 'High-confidence opportunity. All primary signals aligned. Edge sustained across multiple analysis runs.'
            : rec.decisionStatus === 'QUALIFIED_BET'
              ? 'Qualified edge detected. Core signals positive with minor caveats. Monitor for confirmation.'
              : 'Emerging signal. Insufficient consensus for a strong recommendation. Continue monitoring.'}
        </p>
      </div>
    </div>
  );
}

function MarketsTab({ rec }: { rec: SportRecommendation }) {
  return (
    <div className="p-4 space-y-3">
      <div className="card p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Primary Market</div>
            <div className="text-sm font-semibold text-gray-100">{rec.market}</div>
            <div className="text-xs text-gray-400 mt-0.5">{rec.selection}</div>
          </div>
          <span className={clsx('font-mono font-bold tabular-nums text-base', rec.odds > 0 ? 'text-win' : 'text-gray-200')}>
            {rec.odds > 0 ? '+' : ''}{rec.odds}
          </span>
        </div>
      </div>
      <div className="flex items-center justify-center py-6 text-xs text-gray-600">
        <BarChart2 className="w-4 h-4 mr-2" />
        Full market depth available in DiamondEdge
      </div>
    </div>
  );
}

function AITab({ rec }: { rec: SportRecommendation }) {
  const confidence = Math.round((rec.confidence ?? 0) * 100);
  const AGENTS = [
    { name: 'Matchup Analysis', contribution: Math.round(confidence * 0.38), color: 'bg-brand' },
    { name: 'Context & Conditions', contribution: Math.round(confidence * 0.19), color: 'bg-info' },
    { name: 'Recent Form', contribution: Math.round(confidence * 0.13), color: 'bg-win' },
    { name: 'Market Intelligence', contribution: Math.round(confidence * 0.12), color: 'bg-warn' },
    { name: 'ML Model', contribution: Math.round(confidence * 0.10), color: 'bg-purple' },
    { name: 'CLV Edge', contribution: Math.round(confidence * 0.08), color: 'bg-gold' },
  ];

  return (
    <div className="p-4 space-y-3">
      <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">Agent Consensus</div>
      {AGENTS.map((agent) => (
        <div key={agent.name} className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-300">{agent.name}</span>
            <span className="text-xs font-mono text-gray-400">{agent.contribution}%</span>
          </div>
          <div className="h-1.5 bg-surface-3 rounded-full overflow-hidden">
            <div
              className={clsx('h-full rounded-full transition-all', agent.color)}
              style={{ width: `${Math.min(agent.contribution, 100)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function MatchupTab({ rec }: { rec: SportRecommendation }) {
  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 text-center">
          <div className="text-sm font-bold text-gray-100">{rec.awayTeamName}</div>
          <div className="text-xs text-gray-500 mt-0.5">Away</div>
        </div>
        <div className="text-xs font-mono text-gray-600 px-3">@</div>
        <div className="flex-1 text-center">
          <div className="text-sm font-bold text-gray-100">{rec.homeTeamName}</div>
          <div className="text-xs text-gray-500 mt-0.5">Home</div>
        </div>
      </div>
      <div className="flex items-center justify-center gap-2 py-4 text-xs text-gray-600">
        <Cloud className="w-4 h-4" />
        Full matchup intelligence in DiamondEdge
      </div>
    </div>
  );
}

// ─── Main drawer ──────────────────────────────────────────────────────────────

export function GameWorkspaceDrawer() {
  const { workspaceDrawerOpen, workspaceDrawerRec, closeWorkspaceDrawer, openActionSheet } = useAppStore();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [snap, setSnap] = useState<0.6 | 0.9 | 1>(SNAP_60);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [tabDir, setTabDir] = useState<1 | -1>(1);

  // swipe gesture state
  const tabTouchStart = useRef<{ x: number; y: number } | null>(null);

  const y = useMotionValue(0);
  const opacity = useTransform(y, [0, 200], [1, 0]);

  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;

  function snapTo(target: 0.6 | 0.9 | 1) {
    setSnap(target);
    void animate(y, 0, { type: 'spring', stiffness: 400, damping: 40 });
  }

  function handleDragEnd(_: never, info: { offset: { y: number }; velocity: { y: number } }) {
    const { offset, velocity } = info;
    if (velocity.y > 500 || offset.y > 120) {
      if (snap === SNAP_90 || snap === 1) {
        snapTo(SNAP_60);
      } else {
        closeWorkspaceDrawer();
      }
    } else if (velocity.y < -300 || offset.y < -60) {
      if (snap === SNAP_60) snapTo(SNAP_90);
      else if (snap === SNAP_90) snapTo(1);
    } else {
      void animate(y, 0, { type: 'spring', stiffness: 400, damping: 40 });
    }
  }

  function switchTab(dir: 1 | -1) {
    const idx = TABS.findIndex((t) => t.id === activeTab);
    const next = idx + dir;
    if (next < 0 || next >= TABS.length) return;
    setTabDir(dir);
    setActiveTab(TABS[next].id);
  }

  // Horizontal swipe on content to switch tabs; ignore diagonal/vertical gestures
  function onTabTouchStart(e: React.TouchEvent) {
    tabTouchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }
  function onTabTouchEnd(e: React.TouchEvent) {
    if (!tabTouchStart.current) return;
    const dx = e.changedTouches[0].clientX - tabTouchStart.current.x;
    const dy = e.changedTouches[0].clientY - tabTouchStart.current.y;
    tabTouchStart.current = null;
    if (Math.abs(dx) < 40 || Math.abs(dy) > Math.abs(dx) * 0.8) return;
    switchTab(dx < 0 ? 1 : -1);
  }

  useEffect(() => {
    if (!workspaceDrawerOpen) {
      setSnap(SNAP_60);
      setActiveTab('overview');
      y.set(0);
    }
  }, [workspaceDrawerOpen, y]);

  useEffect(() => {
    if (workspaceDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [workspaceDrawerOpen]);

  if (!workspaceDrawerOpen || !workspaceDrawerRec) return null;

  const rec = workspaceDrawerRec;
  const sheetHeight = Math.round(vh * snap);
  const isBetNow = rec.timing === 'BET_NOW';

  return (
    <>
      {/* Backdrop */}
      <motion.div
        className="fixed inset-0 z-50 bg-black/70"
        style={{ opacity }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={closeWorkspaceDrawer}
      />

      {/* Sheet — bottom sheet on mobile, centered modal on desktop */}
      <motion.div
        className={clsx(
          'fixed z-50 flex flex-col bg-surface-1 overflow-hidden',
          isDesktop
            ? 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] max-h-[85vh] rounded-2xl border border-surface-4'
            : 'inset-x-0 bottom-0 rounded-t-2xl',
        )}
        style={isDesktop ? {} : { height: sheetHeight, y, touchAction: 'none' }}
        {...(!isDesktop && {
          drag: 'y' as const,
          dragConstraints: { top: 0, bottom: sheetHeight },
          dragElastic: { top: 0.1, bottom: 0.3 },
          onDragEnd: handleDragEnd,
        })}
        initial={isDesktop ? { opacity: 0, scale: 0.97 } : { y: sheetHeight }}
        animate={isDesktop ? { opacity: 1, scale: 1 } : { y: 0 }}
        exit={isDesktop ? { opacity: 0, scale: 0.97 } : { y: sheetHeight }}
        transition={isDesktop ? { duration: 0.15 } : { type: 'spring', stiffness: 400, damping: 40 }}
      >
        {/* Drag handle (mobile only) */}
        {!isDesktop && (
          <div className="flex items-center justify-center pt-3 pb-2 flex-shrink-0 cursor-grab active:cursor-grabbing">
            <div className="w-10 h-1 rounded-full bg-surface-4" />
          </div>
        )}

        {/* Header */}
        <div className={clsx('flex items-start justify-between px-4 pb-3 flex-shrink-0', isDesktop ? 'pt-4' : 'pt-1')}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-[10px] font-bold uppercase tracking-widest text-brand bg-brand/10 px-1.5 py-0.5 rounded">
                {rec.leagueId.toUpperCase()}
              </span>
              {isBetNow && (
                <span className="text-[10px] font-bold text-gold-bright animate-pulse-slow">● BET NOW</span>
              )}
            </div>
            <p className="text-base font-bold text-gray-100 truncate">
              {rec.awayTeamName} @ {rec.homeTeamName}
            </p>
          </div>
          <div className="flex items-center gap-1 ml-2 flex-shrink-0">
            {!isDesktop && snap === SNAP_60 && (
              <button
                onClick={() => snapTo(SNAP_90)}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-300 hover:bg-surface-3"
                aria-label="Expand"
              >
                <ChevronDown className="w-4 h-4 rotate-180" />
              </button>
            )}
            <button
              onClick={closeWorkspaceDrawer}
              className="p-2 rounded-lg text-gray-500 hover:text-gray-300 hover:bg-surface-3"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tab bar (always visible on desktop; visible at 90%+ snap on mobile) */}
        {(isDesktop || snap !== SNAP_60) && (
          <div className="border-b border-surface-4 flex-shrink-0">
            <div className="flex gap-0 px-4">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    const dir = TABS.findIndex((t) => t.id === tab.id) > TABS.findIndex((t) => t.id === activeTab) ? 1 : -1;
                    setTabDir(dir);
                    setActiveTab(tab.id);
                  }}
                  className={clsx(
                    'flex-1 text-xs font-medium py-2.5 border-b-2 transition-colors',
                    activeTab === tab.id
                      ? 'text-brand border-brand'
                      : 'text-gray-500 border-transparent hover:text-gray-300',
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {/* Tab dot indicator */}
            <div className="flex justify-center gap-1.5 py-1">
              {TABS.map((tab) => (
                <div
                  key={tab.id}
                  className={clsx(
                    'rounded-full transition-all duration-200',
                    activeTab === tab.id ? 'w-3 h-1 bg-brand' : 'w-1 h-1 bg-surface-4',
                  )}
                />
              ))}
            </div>
          </div>
        )}

        {/* Content — swipe left/right to switch tabs */}
        <div
          className="flex-1 overflow-y-auto overscroll-contain"
          onTouchStart={onTabTouchStart}
          onTouchEnd={onTabTouchEnd}
        >
          {!isDesktop && snap === SNAP_60 ? (
            <OverviewTab rec={rec} />
          ) : (
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={activeTab}
                initial={{ x: tabDir * 32, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: tabDir * -32, opacity: 0 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
              >
                {activeTab === 'overview' && <OverviewTab rec={rec} />}
                {activeTab === 'markets'  && <MarketsTab rec={rec} />}
                {activeTab === 'ai'       && <AITab rec={rec} />}
                {activeTab === 'matchup'  && <MatchupTab rec={rec} />}
              </motion.div>
            </AnimatePresence>
          )}
        </div>

        {/* Action footer */}
        <div className="flex gap-2 px-4 py-3 border-t border-surface-4 flex-shrink-0 bg-surface-1">
          <button
            onClick={() => openActionSheet(rec)}
            className="flex-1 py-2.5 rounded-lg text-sm font-medium bg-surface-3 text-gray-200 hover:bg-surface-4 active:scale-95 transition-all"
          >
            Quick Actions
          </button>
          <button
            onClick={() => {
              closeWorkspaceDrawer();
              void navigate(`/baseball/mlb/game/${rec.eventId}`);
            }}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium bg-brand text-white hover:bg-brand-dark active:scale-95 transition-all"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Full View
          </button>
        </div>
      </motion.div>
    </>
  );
}
